import cn from 'classnames'
import { Button, ButtonStyles, Typography } from '@grammarly/badger-ui'
import { composeUrl } from '../../../../../lib/utils/url'
import { useUserService } from '../../../../../lib/hooks'
import styles from './ctas.module.scss'
import { gnarSpec } from '../../../../../lib/tracking/gnar'
import { usePageInfo } from '../../../../../contexts/page-context'
import { FormattedMessage } from 'react-intl'

const getCtaContent = (
  isAnonymous: boolean,
  isInPlanPagePlagiarismExperiment: boolean,
  isInPrecisePlagiarismReportExperiment: boolean
) => {
  if ((isAnonymous && isInPlanPagePlagiarismExperiment) || isInPrecisePlagiarismReportExperiment) {
    return (
      <strong>
        <FormattedMessage id="phrase.getGrammarly" description="Get Grammarly" defaultMessage="Get Grammarly" />
      </strong>
    )
  }

  return isAnonymous ? (
    <>
      <strong>
        <FormattedMessage id="phrase.getGrammarly" description="Get Grammarly" defaultMessage="Get Grammarly" />
      </strong>
      &nbsp;
      <FormattedMessage id="phrase.itsFree" description="Sign up prompt value prop" defaultMessage="It's free" />
    </>
  ) : (
    <strong>
      <FormattedMessage
        description="Get Grammarly Pro"
        defaultMessage="Get Grammarly Pro"
        id="phrase.getGrammarlyPro"
      />
    </strong>
  )
}

type Placement = 'initialSide' | 'wallSide' | 'signupWall' | 'report'

interface AlreadyHaveAnAccountLinkProps {
  placement: Placement
  className?: string
}

export const AlreadyHaveAnAccountLink = (props: AlreadyHaveAnAccountLinkProps) => {
  const { className, placement } = props
  const signinUrl = composeUrl('signin', {})
  const { pageId, slug } = usePageInfo()
  const { isAnonymous } = useUserService().currentUser

  if (!isAnonymous) {
    return null
  }

  return (
    <Typography.C5 className={cn(styles.loginSupplement, className)} as="div">
      <FormattedMessage
        description="Already have an account?"
        defaultMessage="Already have an account?"
        id="identity.account.existing"
      />
      &nbsp;
      <a
        className={styles.loginLink}
        href={signinUrl}
        target="_blank"
        rel="noreferrer noopener"
        onClick={() => {
          void gnarSpec.plagiarismLoginLinkClick(pageId, slug, placement)
        }}
      >
        <FormattedMessage description="Log in" defaultMessage="Log in" id="identity.account.logIn" />
      </a>
    </Typography.C5>
  )
}

interface SignUpButtonProps {
  scaleUp?: boolean
  placement: Placement
  isForPlagiarismPlanPage: boolean
  isInPrecisePlagiarismReportExperiment?: boolean
  className?: string
}

export const SignUpButton = (props: SignUpButtonProps) => {
  const {
    scaleUp,
    placement,
    isForPlagiarismPlanPage,
    isInPrecisePlagiarismReportExperiment = false,
    className,
  } = props
  const { isAnonymous } = useUserService().currentUser
  const signupUrl = composeUrl('signup', {})
  const upgradeUrl = composeUrl('upgrade/pro', {})
  const { pageId, slug } = usePageInfo()

  return (
    <Button
      className={cn(
        ButtonStyles.GreenButtonWhiteText24,
        className,
        styles.ctaButton,
        scaleUp && styles.ctaButtonScaleUp
      )}
      as="a"
      href={isAnonymous ? signupUrl : upgradeUrl}
      onClick={() => {
        const ctaType = isAnonymous ? 'signup' : 'premium'
        void gnarSpec.reportSummaryCtaLinkClick(ctaType, pageId, slug, placement)
      }}
    >
      {getCtaContent(isAnonymous, isForPlagiarismPlanPage, isInPrecisePlagiarismReportExperiment)}
    </Button>
  )
}
